<template>
  <teleport to="body">
    
    <div class="fixed bottom-4 right-4 z-[9999] chat-container support-chat-shell bg-white dark:bg-gray-800 rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-fade-in-up">
      <header class="flex justify-between items-center px-4 py-3 bg-indigo-600 dark:bg-indigo-700 text-white flex-shrink-0">
        <div class="flex items-center">
          <div class="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Icon icon="tabler:help-circle" class="h-6 w-6" />
          </div>
          <span class="font-semibold text-base ml-3">{{ agentName }}</span>
        </div>
        <button @click="closeWidget" class="text-white hover:opacity-80">
          <Icon icon="tabler:x" class="w-6 h-6" />
        </button>
      </header>
      
      <div ref="chatMessages" class="support-chat-body flex-1 overflow-y-auto px-3 py-3 bg-gray-50 dark:bg-gray-800" @click="handleChatClick">
        <div v-if="messages.length === 0 && isLoading === true" class="flex justify-center items-center h-full">
            <svg width="40" height="10" viewBox="0 0 120 30" xmlns="http://www.w3.org/2000/svg" fill="currentColor" class="text-gray-400"><circle cx="15" cy="15" r="15"><animate attributeName="r" from="15" to="15" begin="0s" dur="0.8s" values="15;9;15" calcMode="linear" repeatCount="indefinite" /><animate attributeName="fill-opacity" from="1" to="1" begin="0s" dur="0.8s" values="1;.5;1" calcMode="linear" repeatCount="indefinite" /></circle><circle cx="60" cy="15" r="9" fill-opacity="0.3"><animate attributeName="r" from="9" to="9" begin="0s" dur="0.8s" values="9;15;9" calcMode="linear" repeatCount="indefinite" /><animate attributeName="fill-opacity" from="0.5" to="0.5" begin="0s" dur="0.8s" values=".5;1;.5" calcMode="linear" repeatCount="indefinite" /></circle><circle cx="105" cy="15" r="15"><animate attributeName="r" from="15" to="15" begin="0s" dur="0.8s" values="15;9;15" calcMode="linear" repeatCount="indefinite" /><animate attributeName="fill-opacity" from="1" to="1" begin="0s" dur="0.8s" values="1;.5;1" calcMode="linear" repeatCount="indefinite" /></circle></svg>
        </div>
        <div v-if="messages.length === 0 && isLoading === false" class="text-center py-6 px-2">
          <h3 class="text-base font-medium text-gray-700 dark:text-gray-300 mb-4">{{ t('helpAgent.howCanIHelp', 'How can I help?') }}</h3>
          <div class="grid grid-cols-1 sm:grid-cols-2">
            <button v-for="(prompt, index) in startingPrompts" :key="index" @click="sendStartingPrompt(prompt)" class="support-prompt-button bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 text-sm font-medium px-3 py-2 rounded-lg transition-colors">
              {{ prompt }}
            </button>
          </div>
        </div>
        
        <div v-for="(message, index) in messages" :key="index" :class="['support-message-row', message.type === 'user' ? 'support-message-row-user' : 'support-message-row-agent']">
          <div v-if="message.type === 'agent'" class="support-message-avatar support-message-avatar-agent">
            <Icon icon="tabler:sparkles" class="w-4 h-4" />
          </div>
          <div :class="['support-message-stack', message.type === 'user' ? 'support-message-stack-user' : 'support-message-stack-agent']">
            <div :class="['support-message-label', message.type === 'user' ? 'support-message-label-user' : 'support-message-label-agent']">
              {{ message.type === 'user' ? t('common.you', 'You') : agentName }}
            </div>
            <div :class="[
              'support-message-bubble',
              message.type === 'user' ? 'support-message-bubble-user' : 'support-message-bubble-agent'
            ]">
              <div v-if="message.isLoading === true" class="flex items-center justify-center py-2">
              <svg width="40" height="10" viewBox="0 0 120 30" xmlns="http://www.w3.org/2000/svg" fill="currentColor" class="text-gray-400"><circle cx="15" cy="15" r="15"><animate attributeName="r" from="15" to="15" begin="0s" dur="0.8s" values="15;9;15" calcMode="linear" repeatCount="indefinite" /><animate attributeName="fill-opacity" from="1" to="1" begin="0s" dur="0.8s" values="1;.5;1" calcMode="linear" repeatCount="indefinite" /></circle><circle cx="60" cy="15" r="9" fill-opacity="0.3"><animate attributeName="r" from="9" to="9" begin="0s" dur="0.8s" values="9;15;9" calcMode="linear" repeatCount="indefinite" /><animate attributeName="fill-opacity" from="0.5" to="0.5" begin="0s" dur="0.8s" values=".5;1;.5" calcMode="linear" repeatCount="indefinite" /></circle><circle cx="105" cy="15" r="15"><animate attributeName="r" from="15" to="15" begin="0s" dur="0.8s" values="15;9;15" calcMode="linear" repeatCount="indefinite" /><animate attributeName="fill-opacity" from="1" to="1" begin="0s" dur="0.8s" values="1;.5;1" calcMode="linear" repeatCount="indefinite" /></circle></svg>
              </div>
              <div v-else v-html="sanitizeHtml(message.text)" class="help-chat-markdown prose prose-sm dark:prose-invert max-w-none"></div>
            </div>
          </div>
          <div v-if="message.type === 'user'" class="support-message-avatar support-message-avatar-user">
            <Icon icon="tabler:user" class="w-4 h-4" />
          </div>
        </div>
      </div>
      
      <div class="px-3 py-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/50">
        <div class="flex items-end bg-gray-100 dark:bg-gray-700 rounded-xl px-2 py-2">
          <textarea v-model="userMessage" @keyup.enter.prevent="sendMessage" @input="handleTextareaInput" ref="messageInput" :placeholder="placeholderText" :style="{ direction: inputDirection, textAlign: inputDirection === 'rtl' ? 'right' : 'left' }" class="flex-1 bg-transparent text-gray-800 dark:text-gray-200 px-2 py-2 text-sm resize-none focus:outline-none" rows="1"></textarea>
          <button @click.prevent="sendMessage" class="flex-shrink-0 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg w-9 h-9 flex items-center justify-center transition-colors ml-2">
            <Icon icon="tabler:send" class="w-5 h-5"/>
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { ref, onMounted, nextTick, computed, inject } from 'vue';
import { useRouter } from 'vue-router';
import axios from '@/api/axiosInstance';
import { marked } from 'marked';
import { globalState } from '@/globalState';        
import { Icon } from "@iconify/vue/dist/offline";
import { useI18n } from '@/i18n';
import { sanitizeHtml } from '@/shared/utils/sanitizeHtml';

const { t, currentLanguage } = useI18n();

const renderer = new marked.Renderer();


marked.setOptions({
  renderer,
  gfm: true,
  breaks: true,
});

const emit = defineEmits(['close']);
const router = useRouter();
const agentName = computed(() => t('aiChatAgentSetup.helpAgent.title', 'Help Assistant')); 
const userMessage = ref('');
const messages = ref([]);
const chatMessages = ref(null);
const messageInput = ref(null);
const sessionId = ref(null);
const apiEndpoint = globalState.apiEndpoint;
const inputDirection = ref('ltr');
const isLoading = ref(false);

const placeholderText = computed(() => {
    const appName = globalState.whitelabel?.appName !== null && globalState.whitelabel?.appName !== undefined && globalState.whitelabel?.appName !== '' ? globalState.whitelabel?.appName : 'the application';
    return t('aiChatAgentSetup.helpAgent.placeholderWithApp', { appName }, `Ask about ${appName} features...`);
});

const startingPrompts = computed(() => [
  t('aiChatAgentSetup.helpAgent.startingPrompts.createAgent'),
  t('aiChatAgentSetup.helpAgent.startingPrompts.explainRoles'),
  t('aiChatAgentSetup.helpAgent.startingPrompts.assignToDivision'),
  t('aiChatAgentSetup.helpAgent.startingPrompts.whatAreTools')
]);

const getAuthToken = () => {
  const authToken = document.cookie.split('; ').find(row => row.startsWith('authToken=') !== false)?.split('=')[1];
  const jwtToken = document.cookie.split('; ').find(row => row.startsWith('jwtToken=') !== false)?.split('=')[1];
  const token = authToken !== undefined && authToken !== null && authToken !== '' ? authToken : jwtToken;
  if (token === undefined || token === null) {
    return '';
  }
  return token;
};

const getAuthHeaders = () => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json'
  };
  if (token !== '' && token !== undefined && token !== null) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

const handleAuthError = (error) => {
  if (error.response?.status === 401 || error.response?.status === 403) {
    router.push('/login');
  }
};

const handleChatClick = (event) => {
  const imageTarget = event.target.closest('.help-chat-markdown img');
  if (imageTarget !== null && typeof imageTarget.src === 'string' && imageTarget.src.trim() !== '') {
    window.open(imageTarget.src, '_blank', 'noopener,noreferrer');
    return;
  }
  const target = event.target.closest('a.app-navigation-link');
  if (target !== null && target !== undefined) {
    event.preventDefault();
    const path = target.getAttribute('href');
    if (path !== null && path !== undefined) {
      router.push(path);
      emit('close');
    }
  }
};

const closeWidget = () => {
  emit('close');
};

onMounted(async () => {
  isLoading.value = true;
  try {
    await createNewSession();
  } catch (error) {
    console.error('Initial session creation failed:', error);
    handleAuthError(error);
  } finally {
    isLoading.value = false;
  }
});

async function createNewSession() {
  if (isLoading.value === true && messages.value.length > 0) return;
  isLoading.value = true;

    try {
    const response = await axios.post(`${apiEndpoint}/api/agents/help-agent/createSession`, {}, {
      headers: getAuthHeaders()
    });
    if (response.data?.sessionId !== null && response.data?.sessionId !== undefined && response.data?.sessionId !== '') {
            sessionId.value = response.data.sessionId;
        } else {
       throw new Error("Failed to get session ID");
        }
    } catch (error) {
    console.error('Error creating help session:', error);
        handleAuthError(error);
    } finally {
        isLoading.value = false;
    }
}

function checkInputDirection(text) {
  if (text === null || text === undefined || text === '') {
    inputDirection.value = 'ltr';
    return;
  }
  const rtlRegex = /[\u0590-\u05FF\u0600-\u06FF]/;
  inputDirection.value = rtlRegex.test(text) ? 'rtl' : 'ltr';
}

async function sendStartingPrompt(prompt) {
  userMessage.value = prompt;
  await nextTick();
  adjustTextareaHeight();
  await sendMessage();
}

async function sendMessage() {
  if (userMessage.value === null || userMessage.value === undefined || userMessage.value === '') {
    return;
  }

  const messageText = userMessage.value;
  userMessage.value = '';
  checkInputDirection('');
  await nextTick();
  adjustTextareaHeight();

  appendMessage('user', messageText);
  
  const agentMessageId = `agent-${Date.now()}`;
  appendMessage('agent', '', null, agentMessageId, true);

  await nextTick();
  scrollToBottom();

  try {
    if (sessionId.value === null || sessionId.value === undefined || sessionId.value === '') {
      console.error("Cannot send message: Session ID is missing.");
      updateAgentMessage(agentMessageId, t('aiChatAgentSetup.helpAgent.errorNoSession'));
      return;
    }

    const requestBody = {
      message: messageText,
      language: currentLanguage.value,
    };
    
    const response = await axios.post(`${apiEndpoint}/api/agents/help-agent/${sessionId.value}/query`, requestBody, { headers: getAuthHeaders() });
    
    if (response.data?.answer !== null && response.data?.answer !== undefined && response.data?.answer !== '') {
      const answerHtml = sanitizeHtml(marked.parse(response.data.answer));
      updateAgentMessage(agentMessageId, answerHtml);
    } else {
      updateAgentMessage(agentMessageId, t('aiChatAgentSetup.helpAgent.errorProcessingRequest'));
    }
  } catch (error) {
    console.error('Error sending message:', error);
    handleAuthError(error);
    updateAgentMessage(agentMessageId, t('aiChatAgentSetup.helpAgent.errorGenericWithConsole'));
  }
}

function appendMessage(type, text, files = null, id = null, isLoading = false) {
  const messageData = {
    type,
    text,
    id: id !== null ? id : `msg-${Date.now()}`,
    isLoading,
    direction: isLoading === true ? 'ltr' : getTextDirection(text)
  };
  messages.value.push(messageData);
  nextTick(scrollToBottom);
}

function updateAgentMessage(id, newText) {
  const messageIndex = messages.value.findIndex(msg => msg.id === id && msg.type === 'agent');
  if (messageIndex > -1) {
    messages.value[messageIndex].text = newText;
    messages.value[messageIndex].isLoading = false;
    messages.value[messageIndex].direction = getTextDirection(newText);
    nextTick(scrollToBottom);
  }
}

function scrollToBottom() {
  if (chatMessages.value !== null && chatMessages.value !== undefined && chatMessages.value !== '') {
      chatMessages.value.scrollTop = chatMessages.value.scrollHeight;
    }
}

function getTextDirection(text) {
  if (text === null || text === undefined || text === '') return 'ltr';
  const rtlRegex = /[\u0590-\u05FF\u0600-\u06FF]/;
  return rtlRegex.test(text) ? 'rtl' : 'ltr';
}

function adjustTextareaHeight() {
  if (messageInput.value === null || messageInput.value === undefined) {
    return;
  }
  messageInput.value.style.height = 'auto';
  const nextHeight = Math.min(messageInput.value.scrollHeight, 120);
  messageInput.value.style.height = `${nextHeight}px`;
}

function handleTextareaInput(event) {
  const nextValue = event !== undefined && event !== null && event.target !== undefined && event.target !== null ? event.target.value : userMessage.value;
  checkInputDirection(nextValue);
  adjustTextareaHeight();
}
</script>

<style scoped>
.widget-container {
    position: fixed;
    bottom: 20px;
    right: 20px;
    width: 400px;
    height: 600px;
    display: flex;
    flex-direction: column;
    border-radius: 12px;
    box-shadow: 0 10px 25px rgba(0,0,0,0.1);
    overflow: hidden;
    transition: all 0.3s ease-in-out;
    z-index: 1000;
}
.prose-p\:my-0 p {
  margin-top: 0;
  margin-bottom: 0;
}

.message {
  max-width: 80%;
  padding: 0.75rem 1rem;
  border-radius: 1rem;
  line-height: 1.5;
    opacity: 0;
    transform: translateY(20px);
  animation: messageAppear 0.3s ease forwards;
}

@keyframes messageAppear { to { opacity: 1; transform: translateY(0); } }

.user-message {
  align-self: flex-end;
  background: #4f46e5;
  color: white;
  border-bottom-right-radius: 0.25rem;
}

.agent-message {
  align-self: flex-start;
  background: white;
  color: #1e293b;
  border-bottom-left-radius: 0.25rem;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.dark .agent-message {
  background: #374151;
  color: #f3f4f6;
}

.chat-input {
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  padding: 0.5rem;
  border: 1px solid #e5e7eb;
}

.dark .chat-input {
  background: #1f2937;
  border-color: #4b5563;
}

.input-row {
  display: flex;
  align-items: flex-start;
  width: 100%;
}
.input-row > * + * { margin-left: 0.5rem; }

textarea {
  flex: 1;
  padding: 0.65rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  resize: none;
  min-height: 44px;
  max-height: 120px;
  font-family: inherit;
  font-size: 1rem;
  line-height: 1.5;
  background-color: #f9fafb;
}

.dark textarea {
  background-color: #374151;
  border-color: #4b5563;
  color: #f3f4f6;
}

textarea:focus {
  outline: none;
  border-color: #4f46e5;
  box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.2);
  background-color: white;
}

.dark textarea:focus {
  background-color: #1f2937;
}

.send-button {
  padding: 0.65rem;
  background: #4f46e5;
  color: white;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease-in-out;
  height: 44px;
  flex-shrink: 0;
}
.send-button:hover { background: #4338ca; }
.send-button:active { background: #3730a3; }

.message-content { white-space: pre-wrap; word-wrap: break-word; }

.agent-message.typing .message-content {
  min-height: 30px;
  padding: 0.5rem 1rem;
}

.close-button {
    position: absolute;
    top: 12px;
    right: 12px;
    background: rgba(0,0,0,0.1);
    border: none;
    border-radius: 50%;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background-color 0.2s;
}
.close-button:hover {
    background: rgba(0,0,0,0.2);
}
.dark .close-button {
    background: rgba(255,255,255,0.1);
}
.dark .close-button:hover {
    background: rgba(255,255,255,0.2);
}

a {
  display: inline-flex;
  align-items: center;
  background-color: #3b82f6;
  color: white;
  border-radius: 0.5rem;
  padding: 0.25rem 0.75rem;
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
}
a:hover {
  background-color: #2563eb;
}
a::before {
  content: "👉";
  display: inline-block;
  margin-right: 0.5rem;
}
.support-chat-shell {
  width: min(360px, calc(100vw - 1rem));
  height: min(540px, calc(100vh - 2rem));
  max-height: calc(100vh - 2rem);
}
.support-chat-body > * + * {
  margin-top: 0.875rem;
}
.support-prompt-button {
  margin-top: 0.5rem;
}
.support-prompt-button:nth-child(odd) {
  margin-right: 0.25rem;
}
.support-prompt-button:nth-child(even) {
  margin-left: 0.25rem;
}
.support-message-row {
  display: flex;
  align-items: flex-end;
  width: 100%;
}
.support-message-row + .support-message-row {
  margin-top: 0.875rem;
}
.support-message-row-agent {
  justify-content: flex-start;
}
.support-message-row-user {
  justify-content: flex-end;
}
.support-message-stack {
  display: flex;
  flex-direction: column;
  max-width: calc(100% - 3rem);
}
.support-message-stack-agent {
  align-items: flex-start;
}
.support-message-stack-user {
  align-items: flex-end;
}
.support-message-label {
  font-size: 0.7rem;
  line-height: 1rem;
  font-weight: 600;
  margin-bottom: 0.35rem;
}
.support-message-label-agent {
  color: #6366f1;
  margin-left: 0.2rem;
}
.support-message-label-user {
  color: #475569;
  margin-right: 0.2rem;
}
.support-message-avatar {
  width: 1.9rem;
  height: 1.9rem;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-bottom: 0.15rem;
}
.support-message-avatar-agent {
  background: rgba(99, 102, 241, 0.14);
  color: #4f46e5;
  margin-right: 0.55rem;
}
.support-message-avatar-user {
  background: rgba(15, 23, 42, 0.08);
  color: #334155;
  margin-left: 0.55rem;
}
.support-message-bubble {
  width: fit-content;
  max-width: 100%;
  padding: 0.75rem 0.9rem;
  border-radius: 1rem;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
}
.support-message-bubble-user {
  background: linear-gradient(135deg, #4f46e5 0%, #6366f1 100%);
  color: #ffffff;
  border-bottom-right-radius: 0.3rem;
}
.support-message-bubble-agent {
  background: #ffffff;
  color: #1e293b;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-bottom-left-radius: 0.3rem;
}
.dark .support-message-bubble-agent {
  background: #374151;
  color: #f3f4f6;
  border-color: rgba(148, 163, 184, 0.28);
}
.help-chat-markdown {
  overflow-wrap: anywhere;
}
.support-message-bubble-user .help-chat-markdown {
  color: #ffffff;
}
.support-message-bubble-user .help-chat-markdown :deep(a) {
  color: #e0e7ff;
}
.help-chat-markdown :deep(p) {
  margin-top: 0;
  margin-bottom: 0;
}
.help-chat-markdown :deep(p + p) {
  margin-top: 0.6rem;
}
.help-chat-markdown :deep(img) {
  display: block;
  width: 100%;
  max-width: 240px;
  max-height: 220px;
  object-fit: contain;
  border-radius: 0.9rem;
  border: 1px solid rgba(148, 163, 184, 0.22);
  background: rgba(241, 245, 249, 0.9);
  margin-top: 0.7rem;
  cursor: pointer;
}
.help-chat-markdown :deep(a) {
  word-break: break-word;
}
.help-chat-markdown :deep(a img) {
  margin-top: 0;
}
.dark .help-chat-markdown :deep(img) {
  background: rgba(30, 41, 59, 0.8);
  border-color: rgba(148, 163, 184, 0.32);
}
@media (max-width: 640px) {
  .support-chat-shell {
    right: 0.5rem;
    bottom: 0.5rem;
    width: calc(100vw - 1rem);
    height: min(78vh, 560px);
  }
  .support-message-stack {
    max-width: calc(100% - 2.6rem);
  }
  .support-prompt-button:nth-child(odd) {
    margin-right: 0;
  }
  .support-prompt-button:nth-child(even) {
    margin-left: 0;
  }
}
</style> 